import { useEffect, useMemo } from 'react';
import Flex from '@deprecated-ui/common/Flex';
import Text from '@deprecated-ui/common/Text';
import Button from '@deprecated-ui/common/Button';
import useGetPortfolioData from 'hooks/useGetPortfolioData';
import If from 'global/ErrorHandlers/If';
import {
  clevertapExerciseError,
  clevertapExerciseSelected,
  clevertapExerciseViewed,
} from 'lib/analytics/securities';
import { formatNumber, formatPercentage } from 'utils/number';
import tradingDataStore from 'lib/stores/tradingData';
import NumberInput from 'global/TradingModal/components/NumberInput';
import { lotSize } from 'global/TradingModal/constant';
import { InfoCircleOutlined } from '@ant-design/icons';
import { getFormattedCurrency } from 'global/Securities/utils/currencyFormatter';
import FormItem from '../../components/FormItem';
import { ErrorBox, FormExerciseFieldWrapper } from './styled';
import realTradingStore from '../store';
import { Slider } from '../styled';

interface FormExerciseProps {
  onSubmit: () => void;
}

function FormExercise({ onSubmit }: FormExerciseProps) {
  const {
    getCashOnHand,
    cashOnHand,
    company,
    configFormula: {
      data: { formula },
    },
  } = tradingDataStore();

  const {
    data: { symbol },
  } = company;

  useEffect(() => {
    getCashOnHand();
  }, []);

  const getPortfolioDetail = useGetPortfolioData(symbol);

  const { exercise_data, stock_on_hand } = getPortfolioDetail;
  const { price } = exercise_data;

  const {
    data: { available_cash_on_hand },
  } = cashOnHand;

  const { shares, setShares } = realTradingStore();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- used by eval
  const PRICE = price;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- used by eval
  const LOT = shares / lotSize;
  // eslint-disable-next-line no-eval -- formula passed from the server
  const exerciseAmount = eval(formula.buy_amount_invested);

  const exceedCashOnHand = exerciseAmount > available_cash_on_hand;

  const disableSubmit = useMemo(
    () => shares < 1 || exceedCashOnHand,
    [shares, exceedCashOnHand],
  );

  const exerciseWarningText = (
    <>
      Order Exercise yang sudah disubmit tidak dapat dibatalkan. Setelah
      exercise berhasil dilakukan, kamu hanya bisa menjual di pasar reguler
      dalam satuan lot <b>(1 lot = 100 lembar).</b>
    </>
  );

  useEffect(() => {
    if (exceedCashOnHand) {
      clevertapExerciseError();
    }
  }, [exceedCashOnHand]);

  useEffect(() => {
    setShares(stock_on_hand);
  }, [stock_on_hand]);

  useEffect(
    () => clevertapExerciseViewed({ symbol, share: stock_on_hand }),
    [],
  );

  return (
    <>
      <Flex
        style={{ background: '#fcf6ff', borderRadius: 4 }}
        // @ts-expect-error
        p="8px 12px"
        mb="16px"
      >
        <Text>{exerciseWarningText}</Text>
      </Flex>

      <FormExerciseFieldWrapper>
        <FormItem label="Cash on Hand">
          <Text family="bold">
            {getFormattedCurrency(available_cash_on_hand, {
              currencyPrefix: '',
            })}
          </Text>
        </FormItem>

        <FormItem label="">
          <Flex align="center">
            <Slider
              min={0}
              max={stock_on_hand}
              value={shares}
              tipFormatter={null}
              onChange={(s) => setShares(s)}
            />
            {/* @ts-expect-error */}
            <Text ml="5px" width="80px">
              {formatPercentage((shares / stock_on_hand) * 100, false)}
            </Text>
          </Flex>
        </FormItem>

        <FormItem label="Exercised Amount">
          <Text family="bold">Rp {formatNumber(exerciseAmount, false)}</Text>
        </FormItem>

        <FormItem label="Exercised Price">
          <Text family="bold">Rp {formatNumber(price, false)}</Text>
        </FormItem>

        <FormItem label="Shares Exercised">
          <NumberInput
            defaultValue={stock_on_hand}
            value={shares}
            min={0}
            max={stock_on_hand}
            step={1}
            onChange={(s) => setShares(s)}
          />
        </FormItem>
      </FormExerciseFieldWrapper>

      <If condition={exceedCashOnHand}>
        <ErrorBox>
          <InfoCircleOutlined style={{ color: '#ee4a49', marginRight: 7 }} />
          Nilai exercise kamu melebihi cash on hand yang kamu miliki.
        </ErrorBox>
      </If>

      <Button
        disabled={disableSubmit}
        onClick={() => {
          clevertapExerciseSelected({
            symbol,
            share: shares,
            stockOnHandPercentage: (shares / stock_on_hand) * 100,
          });
          onSubmit();
        }}
        type="primary"
        radius="4px"
        block
        height="40px"
        // @ts-expect-error
        py="12px"
        px="24px"
        style={{
          position: 'absolute',
          left: 0,
          bottom: -36,
          width: '100%',
        }}
      >
        <Text align="inherit" color="inherit" family="bold" size="18px">
          Exercise
        </Text>
      </Button>
    </>
  );
}

export default FormExercise;
